import { DEFAULT_VARIABLE_ATTRIBUTES } from "../../../../shared/utils/projectVariablesChipMerge.utils";
/**
 * Creates a payload builder utility for workflow variables
 * @author Abhishek Dhillon
 */

/**
 * Builds attributes payload from variable context for dropdown type variables
 * @param {Object} contextValues - The variable context values
 * @param {boolean} contextValues.isRequired - Whether the field is required
 * @param {boolean} contextValues.isDefaultSelection - Whether default selection is enabled
 * @param {Object} contextValues.defaultSelection - The default selected option
 * @param {Array} contextValues.defaultOptions - Array of dropdown options
 * @param {boolean} contextValues.optionsView - Whether to show as option buttons
 * @param {boolean} contextValues.multiSelect - Whether to allow multiple selection
 * @returns {Object} The attributes object for dropdown configuration
 */
export const buildDropdownAttributes = (contextValues) => {
  const {
    isRequired,
    isDefaultSelection,
    defaultSelection,
    defaultOptions,
    optionsView,
    isAutomaticallySetValue,
    numberOnly,
    showChipName,
    multiSelect,
    multiDimension,
  } = contextValues;

  return {
    required: isRequired,
    default: isAutomaticallySetValue ? null : isDefaultSelection ? defaultSelection : null,
    options: defaultOptions,
    optionsView: typeof optionsView === "boolean" ? optionsView : typeof optionsView === "string" ? optionsView === "true" : false,
    autoSetValue: isAutomaticallySetValue || false,
    numberOnly: numberOnly,
    showChipName: showChipName !== undefined ? showChipName : true,
    multiSelect: multiSelect,
    multiDimension: multiDimension || false,
  };
};

/**
 * Builds attributes payload from variable context for text type variables
 * @param {Object} contextValues - The variable context values
 * @param {boolean} contextValues.isRequired - Whether the field is required
 * @param {boolean} contextValues.isPlaceholder - Whether placeholder is enabled
 * @param {string} contextValues.placeholderText - The placeholder text
 * @param {boolean} contextValues.isDefaultAnswer - Whether default answer is enabled
 * @param {string} contextValues.defaultValue - The default value
 * @param {boolean} contextValues.isMaxLength - Whether max length is enabled
 * @param {boolean} contextValues.isMinLength - Whether min length is enabled
 * @param {number} contextValues.maxLength - Maximum length value
 * @param {number} contextValues.minLength - Minimum length value
 * @param {boolean} contextValues.isAutomaticallySetValue - Whether value is automatically set
 * @param {boolean} contextValues.isMultiLine - Whether input is multi-line
 * @param {number} contextValues.lineCount - Number of lines for multi-line input
 * @returns {Object} The attributes object for text configuration
 */
export const buildTextAttributes = (contextValues) => {
  const {
    isRequired,
    isPlaceholder,
    placeholderText,
    isDefaultAnswer,
    defaultValue,
    isMaxLength,
    isMinLength,
    maxLength,
    minLength,
    isAutomaticallySetValue,
    isMultiLine,
    lineCount,
    showChipName,
  } = contextValues;
  return {
    required: isRequired,
    placeholder: isAutomaticallySetValue ? "" : isPlaceholder ? placeholderText : "",
    min: isAutomaticallySetValue ? null : isMinLength ? minLength : null,
    max: isAutomaticallySetValue ? null : isMaxLength ? maxLength : null,
    defaultValue: isAutomaticallySetValue ? null : isDefaultAnswer ? defaultValue : null,
    autoSetValue: isAutomaticallySetValue || false,
    isMultiLine: isMultiLine || false,
    lineCount: lineCount || 1,
    showChipName: showChipName !== undefined ? showChipName : true,
  };
};

/**
 * Builds attributes payload from variable context for date type variables
 * @param {Object} contextValues - The variable context values
 * @param {boolean} contextValues.isRequired - Whether the field is required
 * @param {string} contextValues.dateFormat - The date format
 * @param {string} contextValues.dateSeparator - The date separator
 * @param {boolean} contextValues.pastDates - Whether past dates are allowed
 * @param {boolean} contextValues.futureDates - Whether future dates are allowed
 * @param {boolean} contextValues.isStartDate - Whether start date is enabled
 * @param {boolean} contextValues.isEndDate - Whether end date is enabled
 * @param {Date} contextValues.startDate - The start date
 * @param {Date} contextValues.endDate - The end date
 * @returns {Object} The attributes object for date configuration
 */
export const buildDateAttributes = (contextValues) => {
  const {
    isRequired,
    dateFormat,
    dateSeparator,
    pastDates,
    futureDates,
    isStartDate,
    isEndDate,
    startDate,
    endDate,
    isPlaceholder,
    placeholderText,
    isDefaultAnswer,
    defaultValue,
    isAutomaticallySetValue,
    showChipName,
  } = contextValues;

  return {
    placeholder: isAutomaticallySetValue ? "" : isPlaceholder ? placeholderText : "",
    required: isRequired,
    defaultValue: isAutomaticallySetValue ? null : isDefaultAnswer ? defaultValue : null,
    dateFormat: dateFormat ? dateFormat : DEFAULT_VARIABLE_ATTRIBUTES.date.dateFormat,
    dateSeparator: dateSeparator ? dateSeparator : DEFAULT_VARIABLE_ATTRIBUTES.date.dateSeparator,
    pastDates: pastDates,
    futureDates: futureDates,
    startDate: isStartDate ? startDate : null,
    endDate: isEndDate ? endDate : null,
    autoSetValue: isAutomaticallySetValue || false,
    showChipName: showChipName !== undefined ? showChipName : true,
  };
};

/**
 * Builds attributes payload from variable context for number type variables
 * @param {Object} contextValues - The variable context values
 * @param {boolean} contextValues.isRequired - Whether the field is required
 * @param {boolean} contextValues.isDecimal - Whether decimal values are allowed
 * @param {boolean} contextValues.onlyPositiveNumbers - Whether only positive numbers are allowed
 * @param {boolean} contextValues.isDefaultAnswer - Whether default answer is enabled
 * @param {string} contextValues.defaultValue - The default value
 * @returns {Object} The attributes object for number configuration
 */
export const buildNumberAttributes = (contextValues) => {
  const {
    isRequired,
    isDecimal,
    onlyPositiveNumbers,
    isDefaultAnswer,
    defaultValue,
    isPlaceholder,
    placeholderText,
    isMinLength,
    minLength,
    isMaxLength,
    maxLength,
    isAutomaticallySetValue,
    expressionValue,
    showNumberSpinner,
    showChipName,
  } = contextValues;

  const attributes = {
    required: isRequired,
    placeholder: isAutomaticallySetValue ? "" : isPlaceholder ? placeholderText : "",
    min: isAutomaticallySetValue ? null : isMinLength ? minLength : null,
    max: isAutomaticallySetValue ? null : isMaxLength ? maxLength : null,
    defaultValue: isAutomaticallySetValue ? null : isDefaultAnswer ? defaultValue : null,
    decimal: isDecimal,
    onlyPositiveNumbers: onlyPositiveNumbers,
    autoSetValue: isAutomaticallySetValue || false,
    showNumberSpinner: showNumberSpinner !== undefined ? showNumberSpinner : false,
    showChipName: showChipName !== undefined ? showChipName : true,
  };

  return attributes;
};

/**
 * Builds complete variable payload from context values
 * @param {Object} contextValues - The variable context values
 * @param {Object} selectedVariable - The selected variable object
 * @param {Object} workflow - The workflow object
 * @param {string} variableType - The type of variable (dropdown, text, date, number)
 * @returns {Object} The complete payload object
 */
export const buildVariablePayload = (contextValues, selectedVariable, workflow, variableType) => {
  const { variableName, description } = contextValues;
  const { isProjectVariable, projectId } = selectedVariable;
  let attributes = {};

  switch (variableType?.toLowerCase()) {
    case "dropdown":
      attributes = buildDropdownAttributes(contextValues);
      break;
    case "text":
      attributes = buildTextAttributes(contextValues);
      break;
    case "date":
      attributes = buildDateAttributes(contextValues);
      break;
    case "number":
      attributes = buildNumberAttributes(contextValues);
      break;
    default:
      attributes = buildDropdownAttributes(contextValues); // Default fallback
  }
  return {
    id: selectedVariable?.id,
    description: description || selectedVariable?.description,
    name: variableName,
    type: selectedVariable?.variableType ?? selectedVariable?.type,
    workflowId: workflow?.Id,
    attributes,
    adminType: selectedVariable?.adminType,
    adminCustomVariables: selectedVariable?.adminCustomVariables,
    variableId: selectedVariable?.value || selectedVariable?.variableId?.adminVariableId || null,
    ...((isProjectVariable || projectId) ? { projectId: projectId, isProjectVariable: isProjectVariable } : {}),
  };
};

/**
 * Variable-context defaults for team chips / addProjectVariable when there is no workflow UI
 * (same shape as usePayloadBuilder → buildVariablePayload, but without React context).
 */
const DEFAULT_CHIP_CONTEXT_DROPDOWN = {
  isRequired: false,
  isDefaultSelection: false,
  defaultSelection: null,
  defaultOptions: [{ label: "Option 1", value: 1 }],
  optionsView: false,
  isAutomaticallySetValue: false,
  numberOnly: false,
  showChipName: true,
  multiSelect: false,
  multiDimension: false,
};

const DEFAULT_CHIP_CONTEXT_TEXT = {
  isRequired: false,
  isPlaceholder: false,
  placeholderText: "",
  isDefaultAnswer: false,
  defaultValue: null,
  isMaxLength: false,
  isMinLength: false,
  maxLength: null,
  minLength: null,
  isAutomaticallySetValue: false,
  isMultiLine: false,
  lineCount: 1,
  showChipName: true,
};

const DEFAULT_CHIP_CONTEXT_DATE = {
  isRequired: false,
  pastDates: true,
  futureDates: true,
  isAutomaticallySetValue: false,
  showChipName: true,
  isPlaceholder: false,
  placeholderText: "",
  isDefaultAnswer: false,
  defaultValue: null,
  dateFormat: undefined,
  dateSeparator: undefined,
  isStartDate: false,
  isEndDate: false,
  startDate: undefined,
  endDate: undefined,
};

const DEFAULT_CHIP_CONTEXT_NUMBER = {
  isRequired: false,
  isDecimal: false,
  onlyPositiveNumbers: false,
  isDefaultAnswer: false,
  defaultValue: null,
  isPlaceholder: false,
  placeholderText: "",
  isMinLength: false,
  minLength: null,
  isMaxLength: false,
  maxLength: null,
  isAutomaticallySetValue: false,
  expressionValue: undefined,
  showNumberSpinner: false,
  showChipName: true,
};

/**
 * Minimal attributes for addProjectVariable before the chip editor runs.
 * Uses the same attribute builders as {@link buildVariablePayload} / {@link usePayloadBuilder}.
 * @param {string} type - dropdown | text | date | number
 * @returns {Object} attributes payload
 */
export const getDefaultAttributesForChipType = (type) => {
  const t = String(type || "dropdown").toLowerCase();
  switch (t) {
    case "dropdown":
      return buildDropdownAttributes(DEFAULT_CHIP_CONTEXT_DROPDOWN);
    case "text":
      return buildTextAttributes(DEFAULT_CHIP_CONTEXT_TEXT);
    case "date": {
      const attrs = buildDateAttributes(DEFAULT_CHIP_CONTEXT_DATE);
      return {
        required: attrs.required,
        pastDates: attrs.pastDates,
        futureDates: attrs.futureDates,
        autoSetValue: attrs.autoSetValue,
        showChipName: attrs.showChipName,
      };
    }
    case "number":
      return buildNumberAttributes(DEFAULT_CHIP_CONTEXT_NUMBER);
    default:
      return buildDropdownAttributes(DEFAULT_CHIP_CONTEXT_DROPDOWN);
  }
};
